import type * as CSS from 'csstype'
import { system, Config } from '@styled-system/core'
import { ResponsiveValue, TLength } from '../utils/types'
import { getNegativeSpace } from '../utils/getNegativeSpace'
import { getWidthHeight } from '../utils/transforms'

export interface MarginProps {
  /** Margin on top, left, bottom and right */
  m?: ResponsiveValue<CSS.MarginProperty<TLength>>
  /** Margin on top, left, bottom and right */
  margin?: ResponsiveValue<CSS.MarginProperty<TLength>>
  /** Margin on top */
  mt?: ResponsiveValue<CSS.MarginTopProperty<TLength>>
  /** Margin on top */
  marginTop?: ResponsiveValue<CSS.MarginTopProperty<TLength>>
  /** Margin on right */
  mr?: ResponsiveValue<CSS.MarginRightProperty<TLength>>
  /** Margin on right */
  marginRight?: ResponsiveValue<CSS.MarginRightProperty<TLength>>
  /** Margin on bottom */
  mb?: ResponsiveValue<CSS.MarginBottomProperty<TLength>>
  /** Margin on bottom */
  marginBottom?: ResponsiveValue<CSS.MarginBottomProperty<TLength>>
  /** Margin on left */
  ml?: ResponsiveValue<CSS.MarginLeftProperty<TLength>>
  /** Margin on left */
  marginLeft?: ResponsiveValue<CSS.MarginLeftProperty<TLength>>
  /** Margin on left and right */
  mx?: ResponsiveValue<CSS.MarginProperty<TLength>>
  /** Margin on left and right */
  marginX?: ResponsiveValue<CSS.MarginProperty<TLength>>
  /** Margin on top and bottom */
  my?: ResponsiveValue<CSS.MarginProperty<TLength>>
  /** Margin on top and bottom */
  marginY?: ResponsiveValue<CSS.MarginProperty<TLength>>
}

export interface PaddingProps {
  /** Padding on top, left, bottom and right */
  p?: ResponsiveValue<CSS.PaddingProperty<TLength>>
  /** Padding on top, left, bottom and right */
  padding?: ResponsiveValue<CSS.PaddingProperty<TLength>>
  /** Padding on top */
  pt?: ResponsiveValue<CSS.PaddingTopProperty<TLength>>
  /** Padding on top */
  paddingTop?: ResponsiveValue<CSS.PaddingTopProperty<TLength>>
  /** Padding on right */
  pr?: ResponsiveValue<CSS.PaddingRightProperty<TLength>>
  /** Padding on right */
  paddingRight?: ResponsiveValue<CSS.PaddingRightProperty<TLength>>
  /** Padding on bottom */
  pb?: ResponsiveValue<CSS.PaddingBottomProperty<TLength>>
  /** Padding on bottom */
  paddingBottom?: ResponsiveValue<CSS.PaddingBottomProperty<TLength>>
  /** Padding on left */
  pl?: ResponsiveValue<CSS.PaddingLeftProperty<TLength>>
  /** Padding on left */
  paddingLeft?: ResponsiveValue<CSS.PaddingLeftProperty<TLength>>
  /** Padding on left and right */
  px?: ResponsiveValue<CSS.PaddingProperty<TLength>>
  /** Padding on left and right */
  paddingX?: ResponsiveValue<CSS.PaddingProperty<TLength>>
  /** Padding on top and bottom */
  py?: ResponsiveValue<CSS.PaddingProperty<TLength>>
  /** Padding on top and bottom */
  paddingY?: ResponsiveValue<CSS.PaddingProperty<TLength>>
}

export interface CommonProps extends MarginProps, PaddingProps {
  /**
   * The height CSS property specifies the height of an element. By default, the property defines the height of the
   * content area. If box-sizing is set to border-box, however, it instead determines the height of the border area.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/height)
   */
  height?: ResponsiveValue<CSS.HeightProperty<TLength>>
  /**
   *   The width utility parses a component's `width` prop and converts it into a CSS width declaration.
   *
   *   - Numbers from 0-1 are converted to percentage widths.
   *   - Numbers greater than 1 are converted to pixel values.
   *   - String values are passed as raw CSS values.
   *   - And arrays are converted to responsive width styles.
   */
  width?: ResponsiveValue<CSS.WidthProperty<TLength>>
  /**
   * The max-height CSS property sets the maximum height of an element. It prevents the used value of the height
   * property from becoming larger than the value specified for max-height.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/max-height)
   */
  maxHeight?: ResponsiveValue<CSS.MaxHeightProperty<TLength>>
  /**
   * The max-width CSS property sets the maximum width of an element.
   * It prevents the used value of the width property from becoming larger than the value specified by max-width.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/max-width)
   */
  maxWidth?: ResponsiveValue<CSS.MaxWidthProperty<TLength>>
  /**
   * The min-height CSS property sets the minimum height of an element. It prevents the used value of the height
   * property from becoming smaller than the value specified for min-height.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/display)
   */
  minHeight?: ResponsiveValue<CSS.MinHeightProperty<TLength>>
  /**
   * The min-width CSS property sets the minimum width of an element.
   * It prevents the used value of the width property from becoming smaller than the value specified for min-width.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/min-width)
   */
  minWidth?: ResponsiveValue<CSS.MinWidthProperty<TLength>>
  /**
   * The background shorthand CSS property sets all background style properties at once,
   * such as color, image, origin and size, repeat method, and others.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/background)
   */
  background?: ResponsiveValue<CSS.BackgroundProperty<TLength>>
  /**
   * The color utility parses a component's `color` and `backgroundColor` props and converts them into CSS declarations.
   * By default the raw value of the prop is returned.
   *
   * Color palettes can be configured with the ThemeProvider to use keys as prop values, with support for dot notation.
   * Array values are converted into responsive values.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/background-color)
   */
  backgroundColor?: ResponsiveValue<CSS.BackgroundColorProperty>
  /**
   * The color utility parses a component's `color` and `bg` props and converts them into CSS declarations.
   * By default the raw value of the prop is returned.
   *
   * Color palettes can be configured with the ThemeProvider to use keys as prop values, with support for dot notation.
   * Array values are converted into responsive values.
   *
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/background-color)
   */
  bg?: ResponsiveValue<CSS.BackgroundColorProperty>
}

const marginConfig: Config = {
  margin: {
    property: 'margin',
    scale: 'space',
    transform: getNegativeSpace
  },
  marginTop: {
    property: 'marginTop',
    scale: 'space',
    transform: getNegativeSpace
  },
  marginRight: {
    property: 'marginRight',
    scale: 'space',
    transform: getNegativeSpace
  },
  marginBottom: {
    property: 'marginBottom',
    scale: 'space',
    transform: getNegativeSpace
  },
  marginLeft: {
    property: 'marginLeft',
    scale: 'space',
    transform: getNegativeSpace
  },
  marginX: {
    properties: ['marginLeft', 'marginRight'],
    scale: 'space',
    transform: getNegativeSpace
  },
  marginY: {
    properties: ['marginTop', 'marginBottom'],
    scale: 'space',
    transform: getNegativeSpace
  }
}

marginConfig.m = marginConfig.margin
marginConfig.mt = marginConfig.marginTop
marginConfig.mr = marginConfig.marginRight
marginConfig.mb = marginConfig.marginBottom
marginConfig.ml = marginConfig.marginLeft
marginConfig.mx = marginConfig.marginX
marginConfig.my = marginConfig.marginY

export const paddingConfig: Config = {
  padding: {
    property: 'padding',
    scale: 'space'
  },
  paddingTop: {
    property: 'paddingTop',
    scale: 'space'
  },
  paddingRight: {
    property: 'paddingRight',
    scale: 'space'
  },
  paddingBottom: {
    property: 'paddingBottom',
    scale: 'space'
  },
  paddingLeft: {
    property: 'paddingLeft',
    scale: 'space'
  },
  paddingX: {
    properties: ['paddingLeft', 'paddingRight'],
    scale: 'space'
  },
  paddingY: {
    properties: ['paddingTop', 'paddingBottom'],
    scale: 'space'
  }
}

paddingConfig.p = paddingConfig.padding
paddingConfig.pt = paddingConfig.paddingTop
paddingConfig.pr = paddingConfig.paddingRight
paddingConfig.pb = paddingConfig.paddingBottom
paddingConfig.pl = paddingConfig.paddingLeft
paddingConfig.px = paddingConfig.paddingX
paddingConfig.py = paddingConfig.paddingY

const config: Config = {
  ...marginConfig,
  ...paddingConfig,
  height: {
    property: 'height',
    scale: 'sizes',
    transform: getWidthHeight
  },
  width: {
    property: 'width',
    scale: 'sizes',
    transform: getWidthHeight
  },
  maxHeight: {
    property: 'maxHeight',
    scale: 'sizes',
    transform: getWidthHeight
  },
  maxWidth: {
    property: 'maxWidth',
    scale: 'sizes',
    transform: getWidthHeight
  },
  minHeight: {
    property: 'minHeight',
    scale: 'sizes',
    transform: getWidthHeight
  },
  minWidth: {
    property: 'minWidth',
    scale: 'sizes',
    transform: getWidthHeight
  },
  background: true,
  backgroundColor: {
    property: 'backgroundColor',
    scale: 'colors'
  }
}

config.bg = config.backgroundColor

export const commonProps = system(config)

//
// Additional individual exports
//

export const widthProps = system({
  width: {
    property: 'width',
    scale: 'sizes'
  }
})

export interface WidthProps {
  /**
   *   The width utility parses a component's `width` prop and converts it into a CSS width declaration.
   *
   *   - Numbers from 0-1 are converted to percentage widths.
   *   - Numbers greater than 1 are converted to pixel values.
   *   - String values are passed as raw CSS values.
   *   - And arrays are converted to responsive width styles.
   */
  width?: ResponsiveValue<CSS.WidthProperty<TLength>>
}

export const maxHeightProps = system({
  maxHeight: {
    property: 'maxHeight',
    scale: 'sizes'
  }
})

export interface MaxHeightProps {
  /**
   * The max-height CSS property sets the maximum height of an element. It prevents the used value of the height
   * property from becoming larger than the value specified for max-height.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/max-height)
   */
  maxHeight?: ResponsiveValue<CSS.MaxHeightProperty<TLength>>
}

export const marginRightProps = system({
  maxHeight: {
    property: 'maxHeight',
    scale: 'sizes'
  }
})
